import { NgClass, SlicePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges, OnDestroy, OnInit, inject } from '@angular/core';
import { ProductReviewsComponentDef } from '@df/catalog-ui/review/product-reviews.component.def';
import { IProductDataV2, Product } from '@df/catalog/product/product';
import { Review } from '@df/catalog/review/review';
import { ImageModule } from '@df/module-image/image.module';
import { RatingComponent } from '@df/ui/common/rating.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { PagerComponentDef } from '@df/ui/display/pager.component.def';
import { NToggle, ToggleService } from '@df/ui/toggle/toggle.service';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { Subscription } from 'rxjs';
import { ReviewsPagerComponent } from '../../review/reviews-pager.component';
import { ProductReviewsItemComponent } from './item/product-reviews-item.component';
export interface ISortOption {
    name: string;
    sortMethod: (reviews: Review[]) => Review[];
}
@Component({
    selector: 'product-reviews',
    templateUrl: './product-reviews.html',
    standalone: true,
    imports: [
        ImageModule,
        NgClass,
        ProductReviewsItemComponent,
        RatingComponent,
        ReviewsPagerComponent,
        SizeClassDirective,
        SlicePipe,
        TranslationModule
    ],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class ProductReviewsComponent extends ProductReviewsComponentDef implements OnInit, OnDestroy, OnChanges {
    protected subscription: Subscription = new Subscription();
    protected toggleService = inject(ToggleService);
    @Input()
    parent: Product<IProductDataV2>;
    @Input()
    variant: 'standard' | 'modal' = 'standard';
    @Input()
    sortOption: ISortOption;
    displayMoreReviews = true;
    offset = 0;
    pagerSize = 3;
    protected currentSortOption: string;
    protected sortedReviews: Review[];
    override ngOnInit() {
        /**
         * detect changes whenever any status is changed
         */
        this.subscription.add(this.status.subscribe(() => this._ref.detectChanges()));
        this.checkDisplayMoreReviews();
    }
    override ngOnChanges(): void {
        super.ngOnChanges();
        if (this.currentSortOption && this.currentSortOption !== this.sortOption?.name) {
            this.sortReviews(this.sortOption);
        }
    }
    override ngOnDestroy() {
        this.subscription.unsubscribe();
    }
    get reviewsRating(): number {
        return parseFloat((this.parent.data?.reviews?.rating / 20).toFixed(1));
    }
    get reviewsCount(): number {
        return this.parent.data?.reviews?.count;
    }
    /**
     * Reset reviews
     */
    protected override setReviews(reviews?: Review[]): void {
        super.setReviews(reviews);
        if (this.sortOption) {
            this.sortReviews(this.sortOption);
        }
    }
    protected sortReviews(sortOption: ISortOption): void {
        this.sortedReviews = sortOption.sortMethod(this.reviews) || this.reviews;
        this.currentSortOption = sortOption.name;
        this.detectChanges();
    }
    checkDisplayMoreReviews(): void {
        this.displayMoreReviews = this.reviewsCount > this.pagerSize;
    }
    displayMore(): void {
        this.pagerSize = 6;
        this.displayMoreReviews = false;
        this._ref.detectChanges();
    }
    onPagerChange(pager: PagerComponentDef): void {
        this.offset = pager.offset;
        this._ref.detectChanges();
    }
    open(id: NToggle.TId): void {
        this.toggleService.open(id);
    }
    toggle(id: NToggle.TId): void {
        this.toggleService.toggle(id);
    }
}
